/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */

import Client from "../client";
import ResourceCollection from "../resources/resourceCollection";
import ResourceWithId from "../resources/resource";
import { chunk, Dictionary, flatten } from "lodash";
import { RouteArgs } from "client/resolver";

export type ListArgs = {
    skip?: number;
    take?: number;
};

export type AllArgs = {
    ids?: string[];
};

export type ResourcesById<TResource> = { [id: string]: TResource };

// Repositories provide a helpful abstraction around the Octopus Deploy API
class BasicRepository<
    TExistingResource extends ResourceWithId,
    TNewResource, // Should never have a `Links` property, which we rely on in `save`
    TListArgs extends ListArgs & RouteArgs = ListArgs,
    TAllArgs extends AllArgs & RouteArgs = AllArgs,
    TGetArgs extends RouteArgs = {},
    TCreateArgs extends RouteArgs = {},
    TModifyArgs extends RouteArgs = {}
> {
    protected client: Client;
    private readonly collectionLinkName: string;
    private readonly subscribersToDataModifications: Dictionary<(data: TExistingResource) => void>;

    constructor(collectionLinkName: string, client: Client) {
        this.collectionLinkName = collectionLinkName;
        this.client = client;
        this.subscribersToDataModifications = {};
    }

    create(resource: TNewResource, args?: TCreateArgs): Promise<TExistingResource> {
        return this.client.create<TNewResource, TExistingResource>(this.client.getLink(this.collectionLinkName), resource, args!).then(r => this.notifySubscribersToDataModifications(r));
    }
    modify(resource: TExistingResource, args?: TModifyArgs): Promise<TExistingResource> {
        return this.client.update(resource.Links.Self, resource, args).then(r => this.notifySubscribersToDataModifications(r));
    }

    del(resource: TExistingResource) {
        return this.client.del(resource.Links.Self).then(d => this.notifySubscribersToDataModifications(resource));
    }

    list(args?: TListArgs): Promise<ResourceCollection<TExistingResource>> {
        return this.client.get(this.client.getLink(this.collectionLinkName), args);
    }

    get(id: string, args?: TGetArgs): Promise<TExistingResource> {
        const allArgs = this.extend(args || {}, { id });
        return this.client.get(this.client.getLink(this.collectionLinkName), allArgs);
    }

    all(args?: TAllArgs): Promise<TExistingResource[]> {
        if (args !== undefined && args.ids instanceof Array && args.ids.length === 0) {
            return new Promise(res => {
                res([]);
            });
        }

        // http.sys has a max query string of about 16k chars. Our typical max id length is 50 chars
        // so if we are doing requests by id and have more than 300, split into multiple requests
        const maxIds = 300;
        if (args !== undefined && args.ids instanceof Array && args.ids.length > maxIds) {
            return this.batchRequestsById(args, maxIds);
        }

        const allArgs = this.extend(args || {}, { id: "all" });

        return this.client.get(this.client.getLink(this.collectionLinkName), allArgs);
    }

    allById(args?: any): Promise<ResourcesById<TExistingResource>> {
        return this.all(args).then(result =>
            result.reduce((acc: any, resource) => {
                acc[resource.Id] = resource;
                return acc;
            }, {})
        );
    }

    save(resource: TExistingResource | TNewResource): Promise<TExistingResource> {
        if (isExistingResource(resource)) {
            return this.modify(resource);
        } else {
            return this.create(resource);
        }

        function isExistingResource(r: TExistingResource | TNewResource): r is TExistingResource {
            return !!(r as TExistingResource).Links && !!(r as TExistingResource).Id;
        }
    }

    subscribeToDataModifications(key: string, callback: (data: TExistingResource) => void) {
        this.subscribersToDataModifications[key] = callback;
    }

    unsubscribeFromDataModifications(key: string) {
        delete this.subscribersToDataModifications[key];
    }

    protected extend(arg1: any, arg2: any) {
        return { ...arg1, ...arg2 };
    }

    protected notifySubscribersToDataModifications = (resource: TExistingResource): TExistingResource => {
        Object.keys(this.subscribersToDataModifications).forEach(key => this.subscribersToDataModifications[key](resource));

        return resource;
    };

    private batchRequestsById(args: TAllArgs, batchSize: number): Promise<TExistingResource[]> {
        const idArrays = chunk(args!.ids, batchSize);
        const promises: Array<Promise<TExistingResource[]>> = idArrays.map(ids => {
            const newArgs = { ...(args as any), ids, id: "all" };
            return this.client.get(this.client.getLink(this.collectionLinkName), newArgs);
        });
        return Promise.all(promises).then(result => flatten(result));
    }
}

export default BasicRepository;
